"use client";
import React, { useState, useCallback } from "react";
import BlogPostCard from "@/components/pages/blog/BlogPostCard";
import BlogFilters from "@/components/pages/blog/BlogFilters";
import { Pagination } from "@/components/bnp-ui";
import { BlogSearchParams } from "@/types/blog";
import useBlogData from "@/hooks/useBlogData";
import BannerAd from "@/components/shared/ads/BannerAd";
import HeaderAd from "@/components/shared/ads/HeaderAd";
import FooterAd from "@/components/shared/ads/FooterAd";
import InFeedAd from "@/components/shared/ads/InFeedAd";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage } from "@/types/advertising";

const POSTS_PER_PAGE = 12;

export default function BlogIndexPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<BlogSearchParams>({});

  const { bannerAds, inFeedAds, headerAds, footerAds } = usePageAds(
    AdPage.HOME,
  );

  const searchParams: BlogSearchParams = {
    ...filters,
    page: currentPage,
    limit: POSTS_PER_PAGE,
    sortBy: "publishedAt",
    sortOrder: "desc",
    status: "published",
  };

  const { posts, categories, pagination, isLoading, error } =
    useBlogData(searchParams);

  const handleFilterChange = useCallback((newFilters: BlogSearchParams) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "FNP Blog - Nigerian Real Estate Insights",
    description:
      "Expert insights, market analysis, and property guides for Nigerian real estate investors and homebuyers.",
    url: "https://www.findnigeriaproperties.com/blog",
    publisher: {
      "@type": "Organization",
      name: "FNP",
      logo: {
        "@type": "ImageObject",
        url: "https://www.findnigeriaproperties.com/images/logo.png",
      },
    },
    blogPost: posts.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.featuredImage.url,
      datePublished: post.publishedAt,
      author: {
        "@type": "Person",
        name: post.author.name,
      },
      url: `https://www.findnigeriaproperties.com/blog/${post.slug}`,
    })),
  };

  return (
    <>
      {posts.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}

      <div className="min-h-screen bg-grey-1">
        {headerAds[0] && (
          <div className="w-full">
            <HeaderAd ad={headerAds[0]} />
          </div>
        )}
        <div className="bg-main-900 text-white py-16 md:py-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Nigerian Real Estate Insights
              </h1>
              <p className="text-lg md:text-xl text-grey-3 max-w-2xl">
                Expert advice, market trends, and practical guides to help you
                make informed property decisions in Nigeria.
              </p>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <BlogFilters
            categories={categories}
            onFilterChange={handleFilterChange}
            initialFilters={filters}
          />

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-600 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="text-red-900 font-semibold mb-1">
                    Error Loading Blog Posts
                  </h3>
                  <p className="text-red-700 text-sm">
                    {error ||
                      "Failed to load blog posts. Please try again later."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-grey-2 overflow-hidden animate-pulse"
                >
                  <div className="w-full h-48 bg-grey-2" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-grey-2 rounded w-1/4" />
                    <div className="h-6 bg-grey-2 rounded w-3/4" />
                    <div className="h-4 bg-grey-2 rounded w-full" />
                    <div className="h-4 bg-grey-2 rounded w-5/6" />
                    <div className="flex items-center gap-2 pt-3">
                      <div className="w-8 h-8 bg-grey-2 rounded-full" />
                      <div className="h-4 bg-grey-2 rounded w-1/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {posts.map((post, index) => (
                  <React.Fragment key={post.id}>
                    <BlogPostCard post={post} />
                    {/* In-feed ad after every 6th post */}
                    {(index + 1) % 6 === 0 &&
                      inFeedAds[Math.floor(index / 6) % inFeedAds.length] && (
                        <InFeedAd
                          ad={
                            inFeedAds[Math.floor(index / 6) % inFeedAds.length]
                          }
                          className="col-span-full"
                        />
                      )}
                  </React.Fragment>
                ))}
              </div>

              {pagination && pagination.totalPages > 1 && (
                <Pagination
                  metadata={{
                    totalCount: pagination.totalItems,
                    pageSize: pagination.itemsPerPage,
                  }}
                  currentPage={currentPage}
                  pageSize={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                  className="mt-8"
                />
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-grey-2 p-12 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-grey-5 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-xl font-bold text-grey-7 mb-2">
                No blog posts found
              </h3>
              <p className="text-grey-6">
                {filters.query || filters.category
                  ? "Try adjusting your filters to find what you're looking for."
                  : "Check back soon for new content."}
              </p>
              {(filters.query || filters.category) && (
                <button
                  onClick={() => handleFilterChange({})}
                  className="mt-4 bg-main-900 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-main-700 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}

          {/* Internal Banner Ad - always visible regardless of posts */}
          {bannerAds[0] && (
            <div className="w-full my-6">
              <BannerAd ad={bannerAds[0]} />
            </div>
          )}
          {footerAds[0] && (
            <div className="w-full mt-8">
              <FooterAd ad={footerAds[0]} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
